import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Brain, GitPullRequest, AlertTriangle, CheckCircle, XCircle,
  DollarSign, Clock, Zap, Shield, GitBranch, Code, Terminal,
  Bell, Settings, TrendingUp, TrendingDown, Eye, Play, Pause,
  MessageSquare, Github, Gitlab, Box, ArrowRight, RefreshCw,
  FileCode, Server, Database, Cloud, AlertOctagon, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const CostConscience = () => {
  const { shouldShowData } = useDemoData();
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [integrations, setIntegrations] = useState({
    github: { connected: true, repos: 12 },
    gitlab: { connected: true, repos: 8 },
    terraform: { connected: true, workspaces: 5 },
    slack: { connected: true, channels: 3 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Iter84al — ping real backend; rich local demo as fallback.
    if (shouldShowData) {
      const token = localStorage.getItem('token');
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/cost-conscience/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => { /* silent fall-back to local demo */ });
      loadDemoData();
    } else {
      setLoading(false);
    }
  }, [shouldShowData]);

  const loadDemoData = () => {
    setAlerts([
      {
        id: 1,
        type: 'pr',
        title: 'PR #847: Add new ML training pipeline',
        repo: 'acme/ml-platform',
        author: 'sarah.dev',
        costImpact: 12400,
        costType: 'increase',
        status: 'pending',
        timestamp: '2 minutes ago',
        details: {
          resources: [
            { type: 'EC2', name: 'p3.2xlarge x 4', cost: 9800, change: 'new' },
            { type: 'S3', name: 'ml-training-data', cost: 1200, change: 'new' },
            { type: 'EBS', name: '500GB gp3 x 4', cost: 1400, change: 'new' }
          ],
          recommendation: 'Consider using Spot instances for training workloads - could save 70%'
        },
        severity: 'high'
      },
      {
        id: 2,
        type: 'terraform',
        title: 'terraform plan: production/rds-upgrade',
        repo: 'acme/infrastructure',
        author: 'devops-bot',
        costImpact: 3200,
        costType: 'increase',
        status: 'approved',
        timestamp: '1 hour ago',
        details: {
          resources: [
            { type: 'RDS', name: 'db.r5.xlarge → db.r5.2xlarge', cost: 3200, change: 'modify' }
          ],
          recommendation: 'Approved - performance upgrade justified by load analysis'
        },
        severity: 'medium'
      },
      {
        id: 3,
        type: 'pr',
        title: 'PR #845: Optimize image processing',
        repo: 'acme/media-service',
        author: 'john.eng',
        costImpact: -2800,
        costType: 'decrease',
        status: 'approved',
        timestamp: '3 hours ago',
        details: {
          resources: [
            { type: 'Lambda', name: 'image-processor', cost: -1800, change: 'optimize' },
            { type: 'S3', name: 'Intelligent Tiering enabled', cost: -1000, change: 'optimize' }
          ],
          recommendation: 'Great optimization! Auto-approved.'
        },
        severity: 'positive'
      },
      {
        id: 4,
        type: 'deployment',
        title: 'Deploy: staging → production',
        repo: 'acme/api-gateway',
        author: 'ci-pipeline',
        costImpact: 450,
        costType: 'increase',
        status: 'blocked',
        timestamp: '15 minutes ago',
        details: {
          resources: [
            { type: 'ECS', name: 'Scale from 2 to 5 tasks', cost: 450, change: 'scale' }
          ],
          recommendation: 'Blocked: Exceeds daily deployment budget ($300). Requires manager approval.'
        },
        severity: 'blocked'
      },
      {
        id: 5,
        type: 'pr',
        title: 'PR #844: Add Redis caching layer',
        repo: 'acme/backend-api',
        author: 'mike.senior',
        costImpact: 890,
        costType: 'increase',
        status: 'pending',
        timestamp: '30 minutes ago',
        details: {
          resources: [
            { type: 'ElastiCache', name: 'cache.r6g.large x 2', cost: 890, change: 'new' }
          ],
          recommendation: 'Consider: This could reduce RDS load by 40%, potentially saving more than it costs.'
        },
        severity: 'low'
      }
    ]);

    setPolicies([
      { id: 1, name: 'Block high-cost PRs', threshold: 10000, action: 'block', enabled: true, triggered: 3 },
      { id: 2, name: 'Require approval > $5000', threshold: 5000, action: 'require_approval', enabled: true, triggered: 12 },
      { id: 3, name: 'Alert on new GPU instances', type: 'resource', resource: 'p3.*|p4.*|g4.*', action: 'alert', enabled: true, triggered: 5 },
      { id: 4, name: 'Daily deployment budget', threshold: 300, period: 'daily', action: 'block', enabled: true, triggered: 2 },
      { id: 5, name: 'Auto-approve savings', threshold: 0, action: 'auto_approve', enabled: true, triggered: 28 }
    ]);

    setLoading(false);
  };

  const handleApprove = (alertId) => {
    setAlerts(prev => prev.map(a => 
      a.id === alertId ? { ...a, status: 'approved' } : a
    ));
    toast.success('✅ Change approved and proceeding');
  };

  const handleBlock = (alertId) => {
    setAlerts(prev => prev.map(a => 
      a.id === alertId ? { ...a, status: 'blocked' } : a
    ));
    toast.error('🚫 Change blocked');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'positive': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'blocked': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'blocked': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending': return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <Eye className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'pr': return <GitPullRequest className="w-5 h-5" />;
      case 'terraform': return <FileCode className="w-5 h-5" />;
      case 'deployment': return <Play className="w-5 h-5" />;
      default: return <Code className="w-5 h-5" />;
    }
  };

  const pendingAlerts = alerts.filter(a => a.status === 'pending');
  const blockedAlerts = alerts.filter(a => a.status === 'blocked');
  const totalCostImpact = alerts.filter(a => a.status === 'pending').reduce((sum, a) => sum + a.costImpact, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="w-12 h-12 text-emerald-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400">Loading Cost Conscience...</p>
        </div>
      </div>
    );
  }

  if (!shouldShowData) {
    return (
      <div className="space-y-6" data-testid="cost-conscience-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Brain className="h-7 w-7 text-emerald-500" />
              Cost Conscience
            </h1>
            <p className="text-slate-400 dark:text-gray-400 mt-1">
              Pre-decision cost alerts for PRs, Terraform plans, and deployments
            </p>
          </div>
        </div>
        <EmptyDataState title="Cost Conscience" description="Connect your cloud providers and code repositories to get pre-decision cost alerts for PRs, Terraform plans, and deployments." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cost-conscience-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Brain className="h-7 w-7 text-emerald-500" />
            Cost Conscience
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Pre-decision cost alerts for PRs, Terraform plans, and deployments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={pendingAlerts.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
            {pendingAlerts.length} pending review
          </Badge>
        </div>
      </div>

      {/* Stats Banner */}
      {/* KPI strip — v34.45 — all 4 tiles use solid deep colors with dark inner text
          (was bg-gradient which sometimes failed under public theme wrapper, leaving
          labels invisible on white) */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-emerald-500 text-slate-950 border-0 shadow-md shadow-emerald-500/20" data-testid="kpi-pending-review">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-900/80 text-xs font-bold uppercase tracking-wider">Pending Review</p>
                <p className="text-3xl font-extrabold tabular-nums mt-1">{pendingAlerts.length}</p>
              </div>
              <Clock className="w-8 h-8 text-white drop-shadow-sm/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-rose-600 text-white border-0 shadow-md shadow-rose-600/20" data-testid="kpi-blocked">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-rose-50/90 text-xs font-bold uppercase tracking-wider">Blocked</p>
                <p className="text-3xl font-extrabold tabular-nums mt-1 text-white">{blockedAlerts.length}</p>
              </div>
              <AlertOctagon className="w-8 h-8 text-rose-100" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-600 text-white border-0 shadow-md shadow-amber-600/20" data-testid="kpi-pending-cost">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-50/90 text-xs font-bold uppercase tracking-wider">Pending Cost</p>
                <p className="text-3xl font-extrabold tabular-nums mt-1 text-white">${Math.abs(totalCostImpact).toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-100" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-700 text-white border-0 shadow-md shadow-emerald-700/20" data-testid="kpi-saved-month">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-50/90 text-xs font-bold uppercase tracking-wider">Saved This Month</p>
                <p className="text-3xl font-extrabold tabular-nums mt-1 text-white">${shouldShowData ? '47K' : '0'}</p>
              </div>
              <Shield className="w-8 h-8 text-emerald-100" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Live Alerts
            {pendingAlerts.length > 0 && (
              <Badge className="bg-amber-500 text-white text-xs ml-1">{pendingAlerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Cost Policies
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <div className="space-y-4">
            {alerts.map((alert) => (
              <Card 
                key={alert.id} 
                className={`transition-all duration-300 ${
                  alert.status === 'blocked' ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10' :
                  alert.status === 'approved' ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10' :
                  'hover:shadow-lg'
                }`}
                data-testid={`alert-${alert.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Type Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      alert.costType === 'decrease' ? 'bg-emerald-100 text-emerald-600' :
                      alert.severity === 'high' ? 'bg-red-100 text-red-600' :
                      alert.severity === 'blocked' ? 'bg-slate-100 text-slate-400' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      {getTypeIcon(alert.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{alert.title}</h3>
                        <Badge className={getSeverityColor(alert.severity)}>
                          {alert.severity === 'positive' ? 'savings' : alert.severity}
                        </Badge>
                        {getStatusIcon(alert.status)}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-400 mb-3">
                        <span className="flex items-center gap-1">
                          <GitBranch className="w-3 h-3" />
                          {alert.repo}
                        </span>
                        <span>by {alert.author}</span>
                        <span>{alert.timestamp}</span>
                      </div>

                      {/* Resources */}
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 mb-3">
                        <p className="text-xs text-slate-400 mb-2">AFFECTED RESOURCES</p>
                        <div className="space-y-1">
                          {alert.details.resources.map((resource, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                                <span className="text-slate-400 dark:text-slate-400">{resource.name}</span>
                              </span>
                              <span className={resource.cost > 0 ? 'text-red-600 font-medium' : 'text-emerald-600 font-medium'}>
                                {resource.cost > 0 ? '+' : ''}{resource.cost < 0 ? '-' : ''}${Math.abs(resource.cost).toLocaleString()}/mo
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recommendation */}
                      <div className={`text-sm p-3 rounded-lg ${
                        alert.costType === 'decrease' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' :
                        'bg-emerald-50 text-emerald-700 dark:bg-cyan-900/20 dark:text-cyan-400'
                      }`}>
                        <span className="flex items-center gap-2">
                          <Brain className="w-4 h-4" />
                          <strong>AI Recommendation:</strong> {alert.details.recommendation}
                        </span>
                      </div>
                    </div>

                    {/* Cost & Actions */}
                    <div className="text-right flex flex-col items-end gap-3">
                      <div>
                        <p className={`text-2xl font-bold ${
                          alert.costImpact < 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {alert.costImpact > 0 ? '+' : ''}{alert.costImpact < 0 ? '-' : ''}${Math.abs(alert.costImpact).toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-400">per month</p>
                      </div>

                      {alert.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBlock(alert.id)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Block
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(alert.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      )}

                      {alert.status === 'blocked' && (
                        <Badge className="bg-red-100 text-red-700">
                          <XCircle className="w-3 h-3 mr-1" />
                          Blocked
                        </Badge>
                      )}

                      {alert.status === 'approved' && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approved
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Cost Policies</CardTitle>
              <CardDescription>Automatic rules to catch expensive changes before they happen</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {policies.map((policy) => (
                  <div key={policy.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        policy.action === 'block' ? 'bg-red-100 text-red-600' :
                        policy.action === 'require_approval' ? 'bg-amber-100 text-amber-600' :
                        policy.action === 'auto_approve' ? 'bg-emerald-100 text-emerald-600' :
                        'bg-emerald-100 text-emerald-600'
                      }`}>
                        {policy.action === 'block' ? <XCircle className="w-5 h-5" /> :
                         policy.action === 'require_approval' ? <Eye className="w-5 h-5" /> :
                         policy.action === 'auto_approve' ? <Check className="w-5 h-5" /> :
                         <Bell className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{policy.name}</p>
                        <p className="text-sm text-slate-400">
                          {policy.threshold > 0 && `Threshold: $${policy.threshold.toLocaleString()}`}
                          {policy.period && ` / ${policy.period}`}
                          {policy.resource && ` • Pattern: ${policy.resource}`}
                          {' • '}{policy.triggered} triggered
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={
                        policy.action === 'block' ? 'bg-red-100 text-red-700' :
                        policy.action === 'require_approval' ? 'bg-amber-100 text-amber-700' :
                        policy.action === 'auto_approve' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-emerald-100 text-emerald-700'
                      }>
                        {policy.action.replace('_', ' ')}
                      </Badge>
                      <Switch checked={policy.enabled} />
                    </div>
                  </div>
                ))}

                <Button variant="outline" className="w-full mt-4">
                  <Zap className="w-4 h-4 mr-2" />
                  Add New Policy
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* GitHub */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
                      <Github className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">GitHub</h3>
                      <p className="text-sm text-slate-400">PR cost analysis</p>
                    </div>
                  </div>
                  <Badge className={integrations.github.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {integrations.github.connected ? 'Connected' : 'Not connected'}
                  </Badge>
                </div>
                {integrations.github.connected && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400 dark:text-slate-400">
                      Monitoring {integrations.github.repos} repositories
                    </p>
                    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono break-all">
                      Webhook: https://api.cartieai.com/webhooks/github/abc123
                    </div>
                  </div>
                )}
                <Button variant="outline" className="w-full mt-4" size="sm">
                  {integrations.github.connected ? 'Manage Repos' : 'Connect GitHub'}
                </Button>
              </CardContent>
            </Card>

            {/* GitLab */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
                      <Gitlab className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">GitLab</h3>
                      <p className="text-sm text-slate-400">MR cost analysis</p>
                    </div>
                  </div>
                  <Badge className={integrations.gitlab.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {integrations.gitlab.connected ? 'Connected' : 'Not connected'}
                  </Badge>
                </div>
                {integrations.gitlab.connected && (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-slate-400 dark:text-slate-300">
                      Monitoring {integrations.gitlab.repos} repositories
                    </p>
                    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono break-all">
                      Webhook: https://api.cartieai.com/webhooks/gitlab/xyz789
                    </div>
                  </div>
                )}
                <Button variant="outline" className="w-full" size="sm">
                  {integrations.gitlab.connected ? 'Manage Repos' : 'Connect GitLab'}
                </Button>
              </CardContent>
            </Card>

            {/* Terraform */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-cyan-600 flex items-center justify-center">
                      <FileCode className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">Terraform Cloud</h3>
                      <p className="text-sm text-slate-400">Plan cost preview</p>
                    </div>
                  </div>
                  <Badge className={integrations.terraform.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {integrations.terraform.connected ? 'Connected' : 'Not connected'}
                  </Badge>
                </div>
                {integrations.terraform.connected && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400 dark:text-slate-400">
                      Monitoring {integrations.terraform.workspaces} workspaces
                    </p>
                    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono break-all">
                      Run Task URL: https://api.cartieai.com/terraform/cost-check
                    </div>
                  </div>
                )}
                <Button variant="outline" className="w-full mt-4" size="sm">
                  Manage Workspaces
                </Button>
              </CardContent>
            </Card>

            {/* Slack */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#4A154B] flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">Slack</h3>
                      <p className="text-sm text-slate-400">Alert notifications</p>
                    </div>
                  </div>
                  <Badge className={integrations.slack.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {integrations.slack.connected ? 'Connected' : 'Not connected'}
                  </Badge>
                </div>
                {integrations.slack.connected && (
                  <p className="text-sm text-slate-400 dark:text-slate-400">
                    Sending to {integrations.slack.channels} channels
                  </p>
                )}
                <Button variant="outline" className="w-full mt-4" size="sm">
                  Manage Channels
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* How It Works — v34.45 — was cyan icons/labels on emerald (off-brand);
          v34.46 — added inline style={{color:'white'}} on heading to beat the
          shadcn Card's `text-card-foreground` default which was winning the
          cascade and rendering the h3 dark navy. */}
      <Card className="bg-emerald-700 border-0 shadow-lg shadow-emerald-900/20" style={{ color: 'white' }}>
        <CardContent className="p-6" style={{ color: 'white' }}>
          <h3 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ color: 'white' }}>
            <Brain className="w-5 h-5" style={{ color: 'white' }} strokeWidth={2.2} />
            How Cost Conscience Works
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { Icon: GitPullRequest, title: 'PR/MR Created', sub: 'Code pushed to repo' },
              { Icon: Brain, title: 'AI Analysis', sub: 'Scan for cost impact' },
              { Icon: Bell, title: 'Alert Triggered', sub: 'Notify on Slack/PR' },
              { Icon: Shield, title: 'Policy Enforced', sub: 'Block or approve' },
            ].map(({ Icon, title, sub }) => (
              <div key={title} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-900/50 ring-1 ring-white/20 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-6 h-6" style={{ color: 'white' }} strokeWidth={2.2} />
                </div>
                <p className="font-bold" style={{ color: 'white' }}>{title}</p>
                <p className="text-sm mt-0.5" style={{ color: 'rgba(236, 253, 245, 0.85)' }}>{sub}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostConscience;
